/** @jsxImportSource @emotion/react */
import React from "react";
import { Alert, Button, Col, Form, Modal, Row, Typography } from "antd";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Dropzone, { DropzoneRef, useDropzone } from "react-dropzone";
import ReactAvatarEditor from "react-avatar-editor";
import * as Styles from "./styles";
import { UserUsecase } from "../../../usecases/User/usecase";
import AvatarEditor from "react-avatar-editor";
import {
  AuthUsecase,
  useAuthDispatch,
  useAuthState,
} from "../../../usecases/Auth";

interface IFormInputs {
  // Todo: Should define new type for file type in ant design.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  avatar: any;
}

const formSchema = yup.object().shape({
  avatar: yup.mixed().required("Please select image"),
});

interface ModalChangeProfilePhotoProps {
  onCancel: () => void;
  onUpdatedAvatar: () => void;
  visible: boolean;
  imageUrl?: string;
}

const ModalChangeProfilePhotoComponent: React.FC<ModalChangeProfilePhotoProps> =
  ({
    visible,
    onCancel,
    onUpdatedAvatar,
    imageUrl,
  }: ModalChangeProfilePhotoProps) => {
    const authState = useAuthState();
    const authDispatch = useAuthDispatch();
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const {
      handleSubmit,
      formState: { errors },
      reset,
      setValue,
    } = useForm<IFormInputs>({
      mode: "onChange",
      resolver: yupResolver(formSchema),
    });
    const [dropImg, setDropImg] = React.useState(imageUrl);
    const [editing, setEditing] = React.useState(false);
    const [hasImage, setHasImage] = React.useState<boolean>(
      imageUrl !== undefined && imageUrl !== "",
    );
    const [editor, setEditor] = React.useState<AvatarEditor>();
    useDropzone({
      // Disable click and keydown behavior
      noClick: true,
      noKeyboard: true,
    });
    const dropzoneRef = React.createRef<DropzoneRef>();

    const handleDrop = React.useCallback(
      (dropped) => {
        setDropImg(dropped[0]);
        setHasImage(true);
        setValue("avatar", dropped[0]);
      },
      [setValue],
    );

    const avatarEditorRefHandler = React.useCallback((instance) => {
      setEditor(instance);
    }, []);

    const getImageBase64 = React.useCallback(
      (type: string) => {
        return new Promise<string>((resolve, reject) => {
          try {
            if (editor) {
              const canvas = editor.getImage();
              const base64 = canvas.toDataURL(type).split(",")[1];
              resolve(base64);
            }
            resolve("");
          } catch (error) {
            reject(error);
          }
        });
      },
      [editor],
    );

    const handleAvatarPositionChange = React.useCallback(() => {
      setEditing(true);
    }, []);

    const handleUploadButtonClick = React.useCallback(() => {
      dropzoneRef.current?.open();
    }, [dropzoneRef]);

    const handleOnCancel = React.useCallback(() => {
      setDropImg(imageUrl);
      onCancel();
    }, [imageUrl, onCancel]);

    const handleSubmitHandle = React.useCallback(
      async (data: IFormInputs) => {
        setIsProcessing(true);
        try {
          const imageBase64 = await getImageBase64(data.avatar.type);
          const userUsecase = new UserUsecase({ authDispatch });
          await userUsecase.uploadAvatar({
            userId: authState.userInfo?.username,
            imageBase64,
          });

          const authUsecase = new AuthUsecase(authDispatch);
          await authUsecase.syncUserInfo(true);
          onUpdatedAvatar();
          reset();
          setErrorMessage("");
        } catch (error) {
          console.error(error);
          setErrorMessage(error.message);
        } finally {
          setIsProcessing(false);
        }
      },
      [
        authDispatch,
        authState.userInfo,
        getImageBase64,
        onUpdatedAvatar,
        reset,
      ],
    );

    React.useEffect(() => {
      setDropImg(imageUrl);
      setHasImage(imageUrl !== undefined && imageUrl !== "");
    }, [imageUrl]);

    return (
      <Modal
        title="Change Profile Photo"
        visible={visible}
        onCancel={handleOnCancel}
        closable={false}
        footer={
          <Row justify="space-between">
            <Col>
              <Button
                size="large"
                type="link"
                onClick={handleUploadButtonClick}
                css={Styles.buttonLinkBorder}
              >
                Upload photo
              </Button>
            </Col>
            <Col>
              <Button size="large" onClick={handleOnCancel}>
                Cancel
              </Button>
              <Button
                size="large"
                type="primary"
                disabled={!editing}
                onClick={handleSubmit(handleSubmitHandle)}
                loading={isProcessing}
              >
                Save
              </Button>
            </Col>
          </Row>
        }
      >
        <Form
          layout="vertical"
          onFinish={handleSubmit(handleSubmitHandle)}
          id="formUpload"
        >
          {errorMessage && (
            <Row justify="center">
              <Col css={Styles.colGutterStyle}>
                <Alert message={errorMessage} type="error" />
              </Col>
            </Row>
          )}
          <Form.Item
            css={Styles.labelStyle}
            validateStatus={errors.avatar ? "error" : "validating"}
            help={errors.avatar?.message}
          >
            <ReactAvatarEditor
              ref={avatarEditorRefHandler}
              image={dropImg || ""}
              width={416}
              height={416}
              scale={1}
              borderRadius={250}
              color={[243, 243, 244, 0.8]}
              onPositionChange={handleAvatarPositionChange}
            />
            <Dropzone onDrop={handleDrop} ref={dropzoneRef}>
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps()}
                  css={Styles.dropzone}
                  style={{
                    visibility: hasImage ? "hidden" : "visible",
                  }}
                >
                  {!hasImage ? (
                    <div css={Styles.inputLabelContainer}>
                      <Typography.Paragraph css={Styles.fullWidth}>
                        Drag and drop photo here <br />
                        or <u>upload photo</u>
                      </Typography.Paragraph>
                    </div>
                  ) : null}
                  <input {...getInputProps()} />
                </div>
              )}
            </Dropzone>
          </Form.Item>
        </Form>
      </Modal>
    );
  };

export const ModalChangeProfilePhoto = React.memo(
  ModalChangeProfilePhotoComponent,
);
